import { Asset } from '@cube3/common/model/schema/resources/asset';
import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import { compose } from '../../../../../utils/component-helpers';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { removeExtensionFromString } from '@cube3/ui/src/human/getExtensionFromString';
import { useTVBroadcastMetadataConfig } from '../hooks/useTVBroadcastMetadataConfig';
import {
  exportFileNameValidators,
  exportFileNameParser,
  generateCustomExportFileName,
  customFieldValidator,
  lengthValidators,
  videoLengthWarn,
  exportFilenameWarn
} from '../utils/broadcastFieldValidators';
import { findLast } from '../utils/findLast';
import { ExportsParameters } from '@cube3/common/model/schema/resources/export-batch';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import {
  Field,
  Field as FieldConfig
} from '@cube3/forms/src/types/GenericFormTypes';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { isRequired } from '@cube3/forms/src/utils/validators';

interface ReceivedProps {
  asset: Asset;
  metadataFields: FieldConfig[];
  broadcastValues: { [system_name: string]: unknown };
  visible: boolean;
  exportTargets: Array<{ id: string; type: 'export-target' }>;
  exportTargetType: {
    type: 'export-target-type';
    id: string;
  };
  recipients?: string[];
  batchId?: string;
  disableSubmit?: boolean;
}

export interface ExportDataFormValues {
  title: string;
  client: string;
  product_service: string;
  length: number;
  version: string;
  export_filename_checkbox?: boolean;
  export_filename?: string;
  tagon: boolean;
  omruil: boolean;
  low_loudness: boolean;
}

type Props = ReceivedProps &
  InjectedFormProps & {
    formValues: ExportDataFormValues;
  };

const mapStateToProps = (
  state,
  props: ReceivedProps & {
    assetBroadcastValues: { [system_name: string]: unknown };
  }
) => {
  const { asset } = props;

  const form = `tv_broadcast_data_${asset.id}`;
  const values = getFormValues(form)(state);

  return {
    form,
    formValues: values
  };
};

interface WithInitialValuesInjectedProps {
  initialValues: {
    title: string;
    client: string;
    product_service: string;
    version: number;
    export_filename_checkbox: boolean;
  };
  previousExports: ExportsParameters<'broadcast'>[];
}

const withInitialValues = (Wrapped) => (props) => {
  const {
    asset,
    broadcastValues: projectBroadcastValues,
    assetBroadcastValues,
    exportTargets
  } = props;

  const params = useRef({
    filter: {
      export_target_type: props.exportTargetType.id
    }
  });

  const previousExportBatches = useResourceList__ALPHA({
    resourceId: props.asset.id,
    resourceType: props.asset.type,
    edgeType: 'export-batch',
    edgeLabel: 'export-batches',
    params: params.current,
    strategy: 'fetch-on-mount'
  });

  const previousExports = useMemo(() => {
    if (!previousExportBatches.first) return [];
    return previousExportBatches.first?.attributes.exports?.filter(
      (exp) => exp.asset_id === asset?.id
    ) as ExportsParameters<'broadcast'>[];
  }, [previousExportBatches.first, asset]);

  const previousExportsForTargets = previousExports?.filter(
    (e) => !!exportTargets.find((t) => e.target_id === t.id)
  );

  const length = asset.duration
    ? Math.round(asset.duration / 1000)
    : assetBroadcastValues.length
    ? parseInt(assetBroadcastValues.length)
    : undefined;
  const titleFromFile = removeExtensionFromString(asset.display_name);

  const lastVersion =
    assetBroadcastValues.version ||
    findLast('version', previousExportsForTargets, true);

  const initialValues = {
    title: exportFileNameParser(
      assetBroadcastValues.title ||
        findLast('title', previousExports) ||
        titleFromFile
    ),
    client: exportFileNameParser(
      assetBroadcastValues.client ||
        projectBroadcastValues.client ||
        findLast('client', previousExports)
    ),
    product_service: exportFileNameParser(
      assetBroadcastValues.product_service ||
        projectBroadcastValues.product_service ||
        findLast('product_service', previousExports)
    ),
    length,
    version: lastVersion ? parseInt(String(lastVersion)) + 1 : 1,
    export_filename_checkbox: false,
    omruil: false,
    tagon: false,
    low_loudness: false,
    commercial_type: 'Commercial'
  };

  const previousNames = useMemo(
    () =>
      previousExportsForTargets?.map(
        (e) => e.parameters.broadcast.export_filename
      ),
    [previousExportsForTargets]
  );

  if (previousExportBatches.status !== statuses.SUCCESS) {
    return null;
  }

  return (
    <Wrapped
      {...props}
      initialValues={initialValues}
      previousExports={previousExports}
      previousNames={previousNames}
    />
  );
};

/** Create a redux-form for each asset with “broadcast” `metadata-category` fields
 * - prefill metadata fields with project metadata values + asset properties
 */
const TVadExportDataForm: React.FC<Props & WithInitialValuesInjectedProps> = (
  props
) => {
  const { visible, asset, formValues, metadataFields, change } = props;

  const isFilenameEnabled = formValues && formValues.export_filename_checkbox;

  /** The export file name is derived from the values entered in the fields.
   * if checkbox is turned of, make sure that is updated as the values in the fields change.
   */
  const exportFileName = formValues
    ? generateCustomExportFileName(
        formValues.product_service,
        formValues.title,
        formValues.length,
        formValues.omruil ? 'Omruil' : '',
        formValues.tagon ? 'Tagon' : '',
        `VERSION-${formValues.version}`
      )
    : '';

  const initialized = useRef(false);
  useEffect(() => {
    if (isFilenameEnabled) {
      if (!initialized.current && !formValues?.export_filename) {
        change('export_filename', exportFileName);
        initialized.current = true;
      }
    } else {
      change('export_filename', exportFileName);
      initialized.current = true;
    }
  }, [change, exportFileName, formValues, isFilenameEnabled]);

  const fields = useMemo(() => {
    return [
      {
        name: 'length',
        type: 'number',
        groupBy: 'duration_version',
        column: 2,
        label: 'Length in seconds',
        required: true,
        requiredLabel: 'Detected automatically',
        showErrors: true,
        placeholder: 'Enter length in seconds',
        disabled: true,
        warn: videoLengthWarn(asset.duration),
        validate: lengthValidators,
        parse: (val) => val && Number(val)
      } as Field<number, 'number'>,
      {
        name: 'version',
        type: 'number',
        groupBy: 'duration_version',
        column: 2,
        label: 'Version',
        required: true,
        showErrors: true,
        placeholder: 'Enter version of delivery',
        min: 1,
        step: 1,
        validate: isRequired,
        parse: (val) => val && Number(val)
      } as Field<number, 'number'>,
      {
        name: 'commercial_type',
        type: 'select',
        label: 'Commercial type',
        groupBy: 'group_commercial_tag-on',
        column: 2,
        width: 52,
        options: [
          {
            id: 'Commercial',
            value: 'Commercial'
          },
          {
            id: 'Billboard',
            value: 'Billboard'
          },
          {
            id: 'Promo',
            value: 'Promo'
          }
        ]
      } as Field<string, 'select'>,
      {
        name: 'tagon',
        type: 'checkbox',
        label: 'Tag-on',
        groupBy: 'group_commercial_tag-on',
        column: 2,
        text: 'This commercial is a tag-on'
      } as Field<boolean, 'checkbox'>,
      {
        name: 'omruil',
        type: 'checkbox',
        label: 'Replace',
        text: 'This commercial replaces an existing one'
      } as Field<boolean, 'checkbox'>,
      {
        name: 'low_loudness',
        type: 'checkbox',
        label: 'Low Loudness',
        text: 'Use only when audio has been mixed below -23LUFS.'
      } as Field<boolean, 'checkbox'>,
      {
        name: 'export_filename_checkbox',
        type: 'checkbox',
        label: 'Export file name',
        text: 'Use custom export file name'
      } as Field<boolean, 'checkbox'>,
      {
        name: 'export_filename',
        type: 'text',
        // won't show the label, but will still use the space
        label: 'hidden',
        required: false,
        showErrors: true,
        fullWidth: true,
        disabled: !formValues?.export_filename_checkbox,
        placeholder: 'Enter custom export file name',
        warn: exportFilenameWarn,
        validate: exportFileNameValidators
      } as Field<string, 'text'>
    ];
  }, [formValues]);

  const mappedMetadataFields = useMemo(() => {
    return metadataFields?.map((f) => ({
      ...f,
      name: f.system_name,
      label: normalizedFieldName[f.system_name] || f.label,
      validate: customFieldValidator
    }));
  }, [metadataFields]);

  return (
    <div style={{ display: visible ? 'initial' : 'none' }}>
      {/* client, product field */}

      {metadataFields && <GenericReduxForm fields={mappedMetadataFields} />}
      <div style={{ height: 16 }} />
      <GenericReduxForm fields={fields} />
    </div>
  );
};

const normalizedFieldName = {
  client: 'Advertiser',
  product_service: 'Product'
};

const withAssetBroadcastValues = (Wrapped) => (props) => {
  const { broadcastValues: assetBroadcastValues, loading } =
    useTVBroadcastMetadataConfig({
      id: props.asset.id,
      type: props.asset.type
    });

  if (!assetBroadcastValues || loading) {
    return null;
  }

  return <Wrapped {...props} assetBroadcastValues={assetBroadcastValues} />;
};

export default compose(TVadExportDataForm)(
  withAssetBroadcastValues,
  connect(mapStateToProps, {}),
  withInitialValues,
  reduxForm<
    {},
    ReceivedProps & {
      handleSubmit: Function;
    }
  >({
    destroyOnUnmount: false
  })
) as (props: ReceivedProps) => React.ReactElement;
